import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { cartApi, wishlistApi, orderCreationApi, couponApi } from '../lib/supabase-api';
import { devLog } from '../lib/dev-logger';
import { useToast } from '../components/ui/Toast';

// Shipping policy — single source of truth ($15 flat, free over $150)
export const SHIPPING_FEE = 15;
export const FREE_SHIPPING_THRESHOLD = 150;
export const calcShippingFee = (subtotal: number) =>
  subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;

interface CartItem {
  id: string;
  productId: string;
  name: string;
  price: number; // Actual sale price for selected quantity
  originalPrice: number; // Original price for selected quantity
  discount: number;
  image?: string;
  manufacturer: string;
  quantity: number; // Box count (how many boxes to order)
  pillQuantity: number; // Selected pill quantity (40, 100, 200 pills, etc.)
  stock: number;
  addedAt: string;
}

interface ShippingInfo {
  name: string;
  phone: string;
  email: string;
  country?: string;
  address: string;
  detailAddress: string;
  city?: string;
  state?: string;
  zipCode: string;
  deliveryRequest?: string;
  addressEnglish?: string;
  detailAddressEnglish?: string;
  fullAddressEnglish?: string;
  nameEnglish?: string;
}

interface PaymentInfo {
  method: 'card' | 'bank' | 'virtual' | 'phone' | 'crypto';
  cardNumber?: string;
  cardExpiry?: string;
  cardCVC?: string;
  bankAccount?: string;
  bankCode?: string;
  cryptoCoin?: string;
}

interface Order {
  id: string;
  orderNumber: string;
  items: CartItem[];
  subtotal: number;
  shippingFee: number;
  discount: number;
  total: number;
  shippingInfo: ShippingInfo;
  paymentInfo: PaymentInfo;
  status: 'pending' | 'paid' | 'preparing' | 'shipped' | 'delivered' | 'cancelled';
  createdAt: string;
  paidAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
  trackingNumber?: string;
  shippingCompany?: string;
}

interface CartContextType {
  // Cart state
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  shippingFee: number;
  discount: number;
  total: number;
  
  // Cart actions
  addToCart: (product: any, quantity?: number) => void;
  removeFromCart: (productId: string, pillQuantity?: number) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  
  // Checkout
  shippingInfo: ShippingInfo | null;
  paymentInfo: PaymentInfo | null;
  setShippingInfo: (info: ShippingInfo) => void;
  setPaymentInfo: (info: PaymentInfo) => void;
  
  // Orders
  orders: Order[];
  createOrder: () => Promise<string | null>;
  getOrder: (orderId: string) => Order | null;
  
  // Coupons & Discounts
  appliedCoupon: string | null;
  appliedCouponData: any;
  applyCoupon: (couponCode: string) => Promise<boolean>;
  removeCoupon: () => void;
  
  // Wishlist
  wishlist: string[];
  addToWishlist: (productId: string) => void;
  removeFromWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isLoggedIn } = useAuth();
  const toast = useToast();
  const [items, setItems] = useState<CartItem[]>([]);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo | null>(null);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [appliedCouponData, setAppliedCouponData] = useState<any>(null); // DB coupon data storage
  const [wishlist, setWishlist] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Local storage keys (for non-logged-in users)
  const STORAGE_KEYS = {
    CART: 'cart_items',
    SHIPPING: 'shipping_info',
    ORDERS: 'user_orders',
    WISHLIST: 'user_wishlist'
  };

  // Shipping fee values now come from module-level exports (see top of file)

  // Coupon list (USD)
  //   WELCOME10  — flat $10 off, min $20
  //   FIRST20    — 20% off, min $50
  //   VIP10      — flat $10 off, min $80
  const COUPONS = {
    'WELCOME10': { discount: 10, minAmount: 20, name: 'Welcome $10 Off' },
    'FIRST20': { discount: 0.2, minAmount: 50, name: 'First Purchase 20% Off' },
    'VIP10': { discount: 10, minAmount: 80, name: 'VIP $10 Off' }
  };

  // Load user-specific data (Supabase integrated)
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id) {
        // Non-logged-in user: use localStorage
        const savedCart = localStorage.getItem(STORAGE_KEYS.CART);
        if (savedCart) {
          setItems(JSON.parse(savedCart));
        }

        const savedShipping = localStorage.getItem(STORAGE_KEYS.SHIPPING);
        if (savedShipping) {
          setShippingInfo(JSON.parse(savedShipping));
        }

        const savedWishlist = localStorage.getItem(STORAGE_KEYS.WISHLIST);
        if (savedWishlist) {
          setWishlist(JSON.parse(savedWishlist));
        }
        return;
      }

      // Logged-in user: use Supabase
      setIsLoading(true);
      try {
        // Load cart
        const cartItems = await cartApi.getUserCart(user.id);
        setItems(cartItems);

        // Load wishlist
        const wishlistItems = await wishlistApi.getUserWishlist(user.id);
        setWishlist(wishlistItems.map((item: any) => item.productId));

        devLog.info('Supabase cart data loaded successfully', {
          cart: cartItems.length,
          wishlist: wishlistItems.length
        });
      } catch (error) {
        devLog.error('Cart data load failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [user?.id]);

  // Save to localStorage when cart changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(items));
  }, [items]);

  // Save when shipping info changes
  useEffect(() => {
    if (shippingInfo) {
      localStorage.setItem(STORAGE_KEYS.SHIPPING, JSON.stringify(shippingInfo));
    }
  }, [shippingInfo]);

  // Save when order list changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(orders));
  }, [orders]);

  // Save when wishlist changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.WISHLIST, JSON.stringify(wishlist));
  }, [wishlist]);

  // Computed values
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  
  const shippingFee = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;

  let discount = 0;
  if (appliedCoupon) {
    // 1. Check DB coupon first
    if (appliedCouponData) {
      const totalAmount = subtotal + shippingFee;
      if (totalAmount >= (appliedCouponData.min_amount || 0)) {
        if (appliedCouponData.discount_type === 'percentage') {
          discount = subtotal * appliedCouponData.discount_value;
          // Apply max discount amount
          if (appliedCouponData.max_discount) {
            discount = Math.min(discount, appliedCouponData.max_discount);
          }
        } else {
          // fixed type
          discount = appliedCouponData.discount_value;
        }
      }
    }
    // 2. Hardcoded coupons (for non-logged-in users)
    else if (COUPONS[appliedCoupon as keyof typeof COUPONS]) {
      const coupon = COUPONS[appliedCoupon as keyof typeof COUPONS];
      if (subtotal >= coupon.minAmount) {
        if (typeof coupon.discount === 'number' && coupon.discount < 1) {
          discount = subtotal * coupon.discount;
        } else {
          discount = coupon.discount as number;
        }
      }
    }
  }

  const total = Math.max(0, subtotal + shippingFee - discount);

  // Add to cart (Supabase integrated)
  const addToCart = (product: any, quantity: number = 1) => {
    // Price bug fix: prioritize finalPrice and pillQuantity
    const finalPrice = product.finalPrice || product.price;
    const originalPrice = product.originalPrice || product.original_price || finalPrice;
    const pillQuantity = product.pillQuantity || product.pill_quantity || 100;

    if (!user?.id) {
      // Non-logged-in user: existing localStorage logic
      const existingItem = items.find(item => item.productId === product.id && item.pillQuantity === pillQuantity);
      
      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        if (newQuantity <= product.stock) {
          updateQuantity(product.id, newQuantity, pillQuantity);
        } else {
          toast.error(`Insufficient stock. Current stock: ${product.stock}`);
        }
      } else {
        if (quantity <= product.stock) {
          const newItem: CartItem = {
            id: Date.now().toString(),
            productId: product.id,
            name: product.name,
            price: finalPrice,
            originalPrice: originalPrice,
            discount: originalPrice - finalPrice,
            image: product.image,
            manufacturer: product.manufacturer,
            quantity,
            pillQuantity: pillQuantity,
            stock: product.stock,
            addedAt: new Date().toISOString()
          };
          setItems(prev => [...prev, newItem]);
          devLog.info('Added to cart (localStorage):', { 
            productId: product.id,
            name: product.name,
            finalPrice,
            originalPrice,
            pillQuantity,
            quantity 
          });
        } else {
          toast.error(`Insufficient stock. Current stock: ${product.stock}`);
        }
      }
      return;
    }

    // Logged-in user: use Supabase (async processing)
    (async () => {
      try {
        setIsLoading(true);
        const existingItem = items.find(item => item.productId === product.id && item.pillQuantity === pillQuantity);
        
        if (existingItem) {
          const newQuantity = existingItem.quantity + quantity;
          if (newQuantity <= product.stock) {
            const result = await cartApi.updateQuantity(user.id, product.id, newQuantity);
            if (result) {
              setItems(prev => prev.map(item => 
                item.productId === product.id && item.pillQuantity === pillQuantity
                  ? { ...item, quantity: newQuantity }
                  : item
              ));
            }
          } else {
            toast.error(`Insufficient stock. Current stock: ${product.stock}`);
          }
        } else {
          if (quantity <= product.stock) {
            const result = await cartApi.addToCart(user.id, {
              productId: product.id,
              productName: product.name,
              manufacturer: product.manufacturer,
              price: finalPrice,
              originalPrice: originalPrice,
              quantity,
              pillQuantity: pillQuantity,
              image: product.image
            });
            
            if (result) {
              setItems(prev => [...prev, result]);
              devLog.info('Added to cart successfully (Supabase):', { 
                productId: product.id,
                name: product.name,
                finalPrice,
                originalPrice,
                pillQuantity,
                quantity,
                result 
              });
            }
          } else {
            toast.error(`Insufficient stock. Current stock: ${product.stock}`);
          }
        }
      } catch (error) {
        devLog.error('Failed to add to cart:', error);
        toast.error('An error occurred while adding to cart.');
      } finally {
        setIsLoading(false);
      }
    })();
  };

  // Remove from cart (Supabase integrated)
  const removeFromCart = (productId: string, pillQuantity?: number) => {
    if (!user?.id) {
      // Non-logged-in: localStorage - check pill quantity too
      setItems(prev => prev.filter(item => 
        !(item.productId === productId && (!pillQuantity || item.pillQuantity === pillQuantity))
      ));
      devLog.info('Removed from cart (localStorage):', { productId, pillQuantity });
      return;
    }

    // Logged-in: Supabase - check pill quantity too
    (async () => {
      try {
        const success = await cartApi.removeFromCart(user.id, productId);
        if (success) {
          setItems(prev => prev.filter(item => 
            !(item.productId === productId && (!pillQuantity || item.pillQuantity === pillQuantity))
          ));
          devLog.info('Removed from cart (Supabase):', { productId, pillQuantity });
        }
      } catch (error) {
        devLog.error('Failed to remove from cart:', error);
      }
    })();
  };

  // Update quantity (Supabase integrated)
  const updateQuantity = (productId: string, quantity: number, pillQuantity?: number) => {
    if (quantity <= 0) {
      removeFromCart(productId, pillQuantity);
      return;
    }

    if (!user?.id) {
      // Non-logged-in: localStorage - check pill quantity too
      setItems(prev => prev.map(item => {
        if (item.productId === productId && (!pillQuantity || item.pillQuantity === pillQuantity)) {
          if (quantity <= item.stock) {
            return { ...item, quantity };
          } else {
            toast.error(`Insufficient stock. Current stock: ${item.stock}`);
            return item;
          }
        }
        return item;
      }));
      return;
    }

    // Logged-in: Supabase - check pill quantity too
    (async () => {
      try {
        const result = await cartApi.updateQuantity(user.id, productId, quantity);
        if (result) {
          setItems(prev => prev.map(item => 
            item.productId === productId && (!pillQuantity || item.pillQuantity === pillQuantity)
              ? { ...item, quantity }
              : item
          ));
        }
      } catch (error) {
        devLog.error('Failed to update quantity:', error);
      }
    })();
  };

  // Clear cart (Supabase integrated)
  const clearCart = () => {
    if (!user?.id) {
      // Non-logged-in: localStorage
      setItems([]);
      setAppliedCoupon(null);
      setAppliedCouponData(null);
      return;
    }

    // Logged-in: Supabase
    (async () => {
      try {
        const success = await cartApi.clearCart(user.id);
        if (success) {
          setItems([]);
          setAppliedCoupon(null);
          setAppliedCouponData(null);
        }
      } catch (error) {
        devLog.error('Failed to clear cart:', error);
      }
    })();
  };

  // Email sending is handled automatically by DB trigger (no longer called from frontend)
  const sendOrderEmail = async (order: Order): Promise<void> => {
    devLog.info('Email: handled automatically by DB trigger.');
    devLog.info('Order number:', order.orderNumber);
    
    // DB trigger automatically calls Edge Function to send email
    // Handled by notify_new_order() function
  };

  // Create order (Supabase integrated)
  const createOrder = async (): Promise<string | null> => {
    if (items.length === 0 || !shippingInfo || !paymentInfo) {
      return null;
    }

    if (!user?.id) {
      // Non-logged-in user: existing localStorage logic
      const orderNumber = `RM${new Date().getFullYear()}${String(Date.now()).slice(-8)}`;
      const order: Order = {
        id: Date.now().toString(),
        orderNumber,
        items: [...items],
        subtotal,
        shippingFee,
        discount,
        total,
        shippingInfo: { ...shippingInfo },
        paymentInfo: { ...paymentInfo },
        status: 'pending',
        createdAt: new Date().toISOString()
      };

      try {
        await sendOrderEmail(order);
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        order.status = 'paid';
        order.paidAt = new Date().toISOString();
        
        setOrders(prev => [order, ...prev]);
        clearCart();
        
        return order.id;
      } catch (error) {
        devLog.error('Error processing order:', error);
        return null;
      }
    }

    // Logged-in user: use Supabase
    try {
      setIsLoading(true);

      devLog.info('Starting Supabase order creation...');
      const result = await orderCreationApi.createOrder(user.id, {
        items,
        totalAmount: total,
        shippingFee,
        discount,
        shippingInfo,
        paymentMethod: paymentInfo.method
      });

      if (result.success) {
        devLog.info('Order created successfully:', result);
        
        // Send email with actual order info
        devLog.info('Starting email after successful order...');
        if (result.orderNumber) {
          await sendOrderEmail({
            id: result.orderId,
            orderNumber: result.orderNumber,
            items,
            subtotal,
            shippingFee,
            discount,
            total,
            shippingInfo,
            paymentInfo,
            status: 'pending',
            createdAt: new Date().toISOString()
          });
        } else {
          devLog.error('Cannot send email: order number is missing.');
        }

        clearCart(); // Clear cart (already handled by API)
        return result.orderId;
      } else {
        devLog.error('Order creation failed:', result.error);
        toast.error('An error occurred while processing your order: ' + result.error);
        return null;
      }
    } catch (error) {
      devLog.error('Error processing order:', error);
      toast.error('An error occurred while processing your order.');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Order lookup
  const getOrder = (orderId: string): Order | null => {
    return orders.find(order => order.id === orderId) || null;
  };

  // Apply coupon (hardcoded coupons + DB coupon support)
  const applyCoupon = async (couponCode: string): Promise<boolean> => {
    try {
      // DB coupon validation
      if (user?.id) {
        const result = await couponApi.validate(couponCode, user.id, subtotal + shippingFee);
        if (result.valid) {
          setAppliedCoupon(couponCode);
          setAppliedCouponData(result.coupon); // Store coupon data!
          devLog.info('Coupon applied successfully:', result.coupon);
          return true;
        } else {
          toast.error(result.message || 'Coupon is not valid.');
          return false;
        }
      }

      // Non-logged-in: only hardcoded coupons supported
      const coupon = COUPONS[couponCode as keyof typeof COUPONS];
      if (!coupon) {
        toast.error('Invalid coupon code.');
        return false;
      }

      if (subtotal < coupon.minAmount) {
        toast.warning(`Minimum order amount of $${coupon.minAmount.toLocaleString()} required.`);
        return false;
      }

      setAppliedCoupon(couponCode);
      setAppliedCouponData(null); // No data for hardcoded coupons
      return true;
    } catch (error) {
      devLog.error('Coupon application failed:', error);
      toast.error('An error occurred while validating the coupon.');
      return false;
    }
  };

  // Remove coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setAppliedCouponData(null); // Reset coupon data too!
  };

  // Add to wishlist (Supabase integrated)
  const addToWishlist = async (productId: string) => {
    if (!user?.id) {
      // Non-logged-in: localStorage
      if (!wishlist.includes(productId)) {
        setWishlist(prev => [...prev, productId]);
      }
      return;
    }

    // Logged-in: Supabase
    try {
      // Product info needed (find from items or products)
      const productInfo = items.find(item => item.productId === productId);
      if (!productInfo) {
        console.warn('Product info not found, skipping wishlist add:', productId);
        return;
      }

      await wishlistApi.addToWishlist(user.id, {
        productId,
        productName: productInfo.name,
        manufacturer: productInfo.manufacturer,
        price: productInfo.price,
        image: productInfo.image
      });

      if (!wishlist.includes(productId)) {
        setWishlist(prev => [...prev, productId]);
      }
    } catch (error) {
      devLog.error('Failed to add to wishlist:', error);
    }
  };

  // Remove from wishlist (Supabase integrated)
  const removeFromWishlist = async (productId: string) => {
    if (!user?.id) {
      // Non-logged-in: localStorage
      setWishlist(prev => prev.filter(id => id !== productId));
      return;
    }

    // Logged-in: Supabase
    try {
      const success = await wishlistApi.removeFromWishlist(user.id, productId);
      if (success) {
        setWishlist(prev => prev.filter(id => id !== productId));
      }
    } catch (error) {
      devLog.error('Failed to remove from wishlist:', error);
    }
  };

  // Check wishlist
  const isInWishlist = (productId: string): boolean => {
    return wishlist.includes(productId);
  };

  const value: CartContextType = {
    items,
    itemCount,
    subtotal,
    shippingFee,
    discount,
    total,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    shippingInfo,
    paymentInfo,
    setShippingInfo,
    setPaymentInfo,
    orders,
    createOrder,
    getOrder,
    appliedCoupon,
    appliedCouponData,
    applyCoupon,
    removeCoupon,
    wishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};